import React, { createContext, useEffect, useMemo } from "react";
import useList from "hooks/useList";
import type { Notification } from "types";
import CancellablePromise from "common/utils/CancellablePromise";
import useAuth from "hooks/useAuth";

type ProviderNotificationContextType = {
  notifications: Notification[] | null;
  totalUnreadNotifications: number;
  fetchNotifications: (...args: any[]) => CancellablePromise<unknown>;
  isNotificationsFetching: boolean;
};

export const NotificationContext =
  createContext<ProviderNotificationContextType>(
    {} as ProviderNotificationContextType
  );

type Props = {
  children: React.ReactNode;
};

export function NotificationProvider({ children }: Props) {
  const { user } = useAuth();

  const {
    items: notifications,
    fetchItems: fetchNotifications,
    isFetching: isNotificationsFetching,
  } = useList<Notification>("notifications/data/list", {
    listenToEvents: [
      "APPOINTMENT_CREATED_TODAY",
      "APPOINTMENT_UPDATED_IN_PLANNING",
      "REPORT_COMPLETED",
      "APPOINTMENT_FINISHED_WITHOUT_REPORT",
    ],
    ready: Boolean(user?.id),
  });

  useEffect(() => {
    if (user?.id) {
      fetchNotifications();
    }
  }, [user?.id, fetchNotifications]);

  const totalUnreadNotifications = useMemo(
    () => notifications?.filter((n) => !n.isRead)?.length ?? 0,
    [notifications]
  );

  const value = useMemo(
    () => ({
      notifications,
      fetchNotifications,
      isNotificationsFetching,
      totalUnreadNotifications,
    }),
    [
      notifications,
      fetchNotifications,
      isNotificationsFetching,
      totalUnreadNotifications,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}
